import React, { useState, useRef } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col, Spinner, Modal, Table } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { FiArrowLeft, FiUser, FiMail, FiUpload, FiDownload, FiAlertCircle, FiCheckCircle, FiBriefcase, FiTag, FiSave } from 'react-icons/fi';
import api from '../services/api';
import ParticipantPhotoUpload from '../components/ParticipantPhotoUpload';

const CreateParticipant = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const [formData, setFormData] = useState({
        nome: '',
        email: '',
        cpf: '',
        matricula: '',
        tipo: 'Interno',
        departamento: '',
        cargo: ''
    });
    const [biometryFile, setBiometryFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [feedback, setFeedback] = useState({ type: '', msg: '' });
    const [validated, setValidated] = useState(false);
    
    // Import State
    const [showImportModal, setShowImportModal] = useState(false);
    const [importStats, setImportStats] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 10 * 1024 * 1024) { // 10MB
            alert("O arquivo excede o limite de 10MB.");
            return;
        }

        const data = new FormData();
        data.append('file', file);

        setUploading(true);
        try {
            const res = await api.post('/participants/import', data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setImportStats(res.data);
            setShowImportModal(true);
        } catch (err) {
            setFeedback({ type: 'danger', msg: err.response?.data?.msg || "Erro ao processar arquivo." });
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const exportErrors = () => {
        if (!importStats?.erros_detalhes) return;
        
        let csvContent = "data:text/csv;charset=utf-8,Linha,Nome,Erro\n";
        importStats.erros_detalhes.forEach(row => {
            csvContent += `${row.linha},"${row.nome}","${row.erro}"\n`;
        });
        
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "erros_importacao.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        
        if (form.checkValidity() === false) {
            e.stopPropagation();
            setValidated(true);
            return;
        }

        setValidated(true);
        setLoading(true);
        setFeedback({ type: '', msg: '' });

        try {
            // 1. Create Participant
            const res = await api.post('/participants', formData);
            const participantId = res.data.id;

            // 2. Upload Biometry (if selected)
            if (biometryFile && participantId) {
                const bioData = new FormData();
                bioData.append('file', biometryFile);
                
                try {
                    await api.post(`/participants/${participantId}/biometry`, bioData);
                } catch (bioErr) {
                    console.error("Biometry upload failed", bioErr);
                    // Don't block success, just warn
                    const errorMsg = bioErr.response?.data?.msg || bioErr.message || "Erro desconhecido";
                    alert(`Participante criado, mas erro ao enviar foto: ${errorMsg}`);
                }
            }

            setFeedback({ type: 'success', msg: 'Participante cadastrado com sucesso! Redirecionando...' });
            setTimeout(() => navigate('/participant-management'), 2000);
        } catch (err) {
            const errorMsg = err.response?.data?.msg || 'Erro ao cadastrar participante. Tente novamente.';
            setFeedback({ type: 'danger', msg: errorMsg });
            setLoading(false);
        }
    };

    return (
        <Container className="py-5" style={{ maxWidth: '800px' }}>
            <div className="mb-4 d-flex align-items-center justify-content-between">
                <div>
                    <Button variant="link" as={Link} to="/participant-management" className="text-decoration-none p-0 mb-1 text-muted">
                        <FiArrowLeft /> Voltar para Gestão
                    </Button>
                    <h2 className="fw-bold">Novo Participante</h2>
                    <p className="text-muted">Preencha os dados abaixo ou utilize a importação em massa.</p>
                </div>
            </div>

            {/* Import Card */}
            <Card className="shadow-sm border-0 rounded-4 mb-4 bg-light">
                <Card.Body className="p-4 d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <div>
                        <h5 className="fw-bold mb-1 text-primary"><FiUpload className="me-2"/> Cadastro em Massa</h5>
                        <p className="mb-0 text-muted small">Importe múltiplos participantes via planilha Excel.</p>
                    </div>
                    <div className="d-flex gap-2">
                        <a href="/files/Modelo_Cadastro_Participante.xlsx" download className="btn btn-outline-secondary d-flex align-items-center gap-2">
                            <FiDownload /> Baixar Modelo
                        </a>
                        <Button variant="primary" onClick={() => fileInputRef.current.click()} disabled={uploading} className="d-flex align-items-center gap-2">
                            {uploading ? <Spinner size="sm" animation="border" /> : <FiUpload />} 
                            Importar Planilha
                        </Button>
                        <input 
                            type="file" 
                            ref={fileInputRef} 
                            style={{ display: 'none' }} 
                            accept=".xlsx"
                            onChange={handleFileUpload}
                        />
                    </div>
                </Card.Body>
            </Card>

            <Card className="shadow-sm border-0 rounded-4">
                <Card.Body className="p-4 p-md-5">
                    {feedback.msg && <Alert variant={feedback.type} dismissible onClose={() => setFeedback({ type: '', msg: '' })}>{feedback.msg}</Alert>}
                    
                    <Form noValidate validated={validated} onSubmit={handleSubmit}>
                        <div className="d-flex justify-content-center mb-4">
                            <ParticipantPhotoUpload 
                                onFileSelect={setBiometryFile}
                            />
                        </div>
                        <Row className="g-4">
                            <Col md={12}>
                                <h5 className="mb-3 text-primary d-flex align-items-center gap-2">
                                    <FiUser /> Dados Pessoais (Cadastro Individual)
                                </h5>
                            </Col>
                            
                            <Col md={12}>
                                <Form.Group controlId="nome">
                                    <Form.Label>Nome Completo</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="nome"
                                        value={formData.nome}
                                        onChange={handleChange}
                                        placeholder="Ex: Maria Oliveira"
                                        required 
                                        className="py-2"
                                    />
                                    <Form.Control.Feedback type="invalid">Por favor, informe o nome completo.</Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col md={12}>
                                <Form.Group controlId="email">
                                    <Form.Label>Email</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0"><FiMail /></span>
                                        <Form.Control 
                                        type="email" 
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="email@exemplo.com"
                                        className="py-2 border-start-0"
                                    />
                                    <Form.Control.Feedback type="invalid">Informe um email válido.</Form.Control.Feedback>
                                    </div>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="cpf">
                                    <Form.Label>CPF</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="cpf"
                                        value={formData.cpf}
                                        onChange={handleChange}
                                        placeholder="000.000.000-00"
                                        className="py-2"
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="matricula">
                                    <Form.Label>Matrícula (Opcional)</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="matricula"
                                        value={formData.matricula}
                                        onChange={handleChange}
                                        placeholder="Ex: 987654"
                                        className="py-2"
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={12}>
                                <hr className="my-2 opacity-10" />
                                <h5 className="mb-3 mt-2 text-primary d-flex align-items-center gap-2">
                                    <FiBriefcase /> Dados Corporativos
                                </h5>
                            </Col>

                            <Col md={4}>
                                <Form.Group controlId="tipo">
                                    <Form.Label>Tipo</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0"><FiTag /></span>
                                        <Form.Select 
                                            name="tipo"
                                            value={formData.tipo}
                                            onChange={handleChange}
                                            className="py-2 border-start-0"
                                        >
                                            <option value="Interno">Interno</option>
                                            <option value="Externo">Externo</option>
                                        </Form.Select>
                                    </div>
                                </Form.Group>
                            </Col>

                            <Col md={4}>
                                <Form.Group controlId="departamento">
                                    <Form.Label>Departamento</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="departamento"
                                        value={formData.departamento}
                                        onChange={handleChange}
                                        placeholder="Ex: Recursos Humanos"
                                        className="py-2"
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={4}>
                                <Form.Group controlId="cargo">
                                    <Form.Label>Cargo</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="cargo"
                                        value={formData.cargo}
                                        onChange={handleChange}
                                        placeholder="Ex: Analista"
                                        className="py-2"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end mt-5 gap-3">
                            <Button variant="light" as={Link} to="/participant-management" className="px-4">
                                Cancelar
                            </Button>
                            <Button variant="primary" type="submit" disabled={loading} className="px-4 d-flex align-items-center gap-2">
                                {loading ? <Spinner size="sm" animation="border" /> : <FiSave />}
                                {loading ? 'Salvando...' : 'Cadastrar Participante'}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>

            {/* Import Results Modal */}
            <Modal show={showImportModal} onHide={() => setShowImportModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Resultado da Importação</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {importStats && (
                        <div>
                            <Row className="mb-4 text-center">
                                <Col>
                                    <div className="p-3 bg-light rounded-3">
                                        <h3 className="fw-bold text-primary">{importStats.total_lidos}</h3>
                                        <span className="text-muted small">Lidos</span>
                                    </div>
                                </Col>
                                <Col>
                                    <div className="p-3 bg-success bg-opacity-10 rounded-3 text-success">
                                        <h3 className="fw-bold">{importStats.sucessos}</h3>
                                        <span className="small"><FiCheckCircle /> Sucessos</span>
                                    </div>
                                </Col>
                                <Col>
                                    <div className="p-3 bg-danger bg-opacity-10 rounded-3 text-danger">
                                        <h3 className="fw-bold">{importStats.falhas}</h3>
                                        <span className="small"><FiAlertCircle /> Falhas</span>
                                    </div>
                                </Col>
                            </Row>

                            {importStats.falhas > 0 && (
                                <>
                                    <h6 className="fw-bold text-danger mb-3">Detalhamento dos Erros:</h6>
                                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                        <Table size="sm" striped bordered hover className="mb-0">
                                            <thead className="bg-light sticky-top">
                                                <tr>
                                                    <th style={{width: '60px'}}>Linha</th>
                                                    <th>Nome</th>
                                                    <th>Motivo do Erro</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {importStats.erros_detalhes.map((err, idx) => (
                                                    <tr key={idx}>
                                                        <td className="text-center">{err.linha}</td>
                                                        <td>{err.nome || '-'}</td>
                                                        <td className="text-danger small">{err.erro}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {importStats?.falhas > 0 && (
                        <Button variant="outline-danger" onClick={exportErrors}>
                            <FiDownload className="me-2" /> Exportar Erros (CSV)
                        </Button>
                    )}
                    <Button variant="secondary" onClick={() => setShowImportModal(false)}>
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default CreateParticipant;